/**
 * AddressesCard — the client's addresses with the "currently at" model
 * (0137, WS-4 D2; team feedback 2026-07-10: "where do I enter an
 * address?" + the CareTree poison of CURRENTLY AT jammed into address
 * line 2).
 *
 * Renders from the aggregate snapshot (record.addresses — no own fetch,
 * the PR-C2 contract); mutations go through clientAddressesApi and
 * invalidate ['clientRecord'] for one audited re-read (the RowEditDialog
 * posture). Not a ProjectionCard row list: addresses carry no
 * note-pipeline provenance (operator-entered, change_log-trailed
 * server-side), so the card is the BillingDetailsCard shape — a plain
 * card with an in-file dialog.
 *
 * Semantics surfaced: the PRIMARY address is the client's home (the
 * canonical/mailing identity address); the CURRENT LOCATION is where the
 * client is RIGHT NOW when that differs (facility, rehab, family). The
 * summary reads both through src/lib/clientLocation.ts, the same rules
 * the Contact band and the face sheet use. Setting either flag clears
 * the previous holder server-side, in one transaction. Archived rows
 * render muted (soft-archive; nothing is deleted).
 *
 * A save whose text changed (a create included) is checked first
 * (useAddressCheck, POST /address-check): a match saves; a different
 * match asks "Did you mean", with "Use this" and "Keep what I typed"; no
 * match says so, with "Save as typed", except for a currently-at address
 * that is not the home, which saves as after a match. A check that cannot
 * run never stops the save, and the dialog then says the address could
 * not be checked. Every field is locked while the check runs, any change clears
 * a prompt, and Cancel writes nothing, before or after it. No check is
 * sent for a PO Box line 1 (the geocoder places it at town level,
 * approximate by design) or by a seat the check refuses (canCheckAddress:
 * neither write_clinical_notes nor suggest); those save straight away and
 * the save's own outcome line says what happened.
 *
 * A save reports what it did to the map (geocode_outcome). A placed
 * address, or a save that did not change the text, closes the dialog as
 * any save does; an approximate, not-found or unchecked address keeps it
 * open to say so (GeocodeOutcomeLine), as does a saved-as-typed address
 * the save placed, with the fields locked and only Done to close. A
 * not-found address saved with no state asks for the state, naming the
 * tenant's working states.
 *
 * A home (the primary) needs a street, city, state and ZIP, and a new
 * address's State starts on the tenant's first working state, or needs
 * choosing when none is known. The card archives any active row, the
 * primary included; Edit contact removes the home through the same
 * archive route.
 */

import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { suggestAddressParts } from '@/lib/addressSuggestion';
import { useParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, Pencil } from 'lucide-react';
import {
  useArchivedItems,
  CARD_ACCENTS,
  usePersistedCardOpen,
} from '@/components/clientRecord/cardSpine';
import { Button } from '@/components/primitives/Button';
import { Chip } from '@/components/primitives/Chip';
import { Dialog } from '@/components/primitives/Dialog';
import { Field, fieldBoxAria } from '@/components/primitives/Field';
import { FLD } from '@/components/primitives/modalFormStyles';
import { SelectField } from '@/components/primitives/SelectField';
import { TextInput } from '@/components/primitives/TextInput';
import { CARE_SETTING_PLACE_TYPES, PLACE_TYPES, PLACE_TYPE_LABEL_KIND } from '@/lib/noteVocabulary';
import { humanizeToken } from '@/lib/fieldLabels';
import { PLACE_TYPE_WORDS } from '@/lib/recordFormWords';
import { pronounForms } from '@/lib/pronounForms';
import { proseDate } from '@/lib/proseDate';
import { useTenant } from '@/lib/auth';
import { canCheckAddress, canManageClientIdentity, canWriteClinicalNotes, canWriteClinicalRecord } from '@/lib/capabilities';
import { api } from '@/lib/api';
import { DirectoryNameBox } from '@/components/clientRecord/DirectoryNameBox';
import {
  checkClientAddress,
  useSaveClientAddress,
  useSetClientAddressArchived,
  type AddressCheckFields,
  type ClientAddressGeocodeOutcome,
  type ClientAddressInput,
} from '@/lib/clientAddressesApi';
import { ApiError } from '@/lib/api';
import { contactSentence, saveFailureText, serverSentence, type SupportContact } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import type { AddressRow, ClientPronouns } from '@/lib/clientRecord';
import {
  addressLine,
  addressTextChanged,
  formatAddress,
  formatCityStateZip,
  isPoBoxLine,
  missingHomeFields,
  newAddressStartState,
  zipProblem,
} from '@/lib/address';
import { fieldSentence } from '@/lib/fieldErrorCopy';
import { NO_PRIMARY_TEXT, currentlyAt, homeFact, notPlacedLabel } from '@/lib/clientLocation';
import { readWorkingStates, type WorkingState } from '@/lib/tenantConfig';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { US_STATES, US_TERRITORIES, isUsStateCode, normalizeZip, typedStateCode } from '@/lib/usStates';
import { DateInput } from '@/components/primitives/DateInput';

/** True for the 409 the address routes answer when the client already
 *  has an active address with the same text. */
function isDuplicateAddress(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 409 &&
    (err.body as { error?: string } | null)?.error === 'duplicate_address'
  );
}

/** EXPORTED: the contact dialog (VitalsBand's ReachEditDialog) reports an
 *  address-save failure through the same copy, so one wording covers both
 *  doors onto the address route. A 400 invalid_value naming missing
 *  fields (a home without a street, city, state or ZIP) says so, checked
 *  first; one on 'state' or 'zip' (the repo's state/ZIP refusal) gets the
 *  field's own fix-forward wording; every other 400, including the
 *  validator's length refusals (which also carry a field), keeps the
 *  generic copy. A 409 duplicate_address names the duplicate; any other
 *  409 is a race. */
export function saveErrorMessage(err: unknown, contact: SupportContact): string {
  // A seat that reads the record without keeping it (the server's
  // care_record_read_only) reads the server's own sentence.
  const said = serverSentence(err, 'record_edits');
  if (said !== null) return said;
  if (err instanceof ApiError) {
    if (err.status === 403) {
      return contactSentence("Changing addresses isn't part of your access. Ask {contact_admin} if you need it.", contact);
    }
    if (err.status === 400) {
      const body = err.body as { error?: string; field?: string; missing?: unknown } | null;
      if (body?.error === 'invalid_value') {
        if (Array.isArray(body.missing) && body.missing.length > 0) return HOME_INCOMPLETE_TEXT;
        if (body.field === 'state') return 'Choose a state from the list.';
        if (body.field === 'zip') return 'Enter a five-digit ZIP, or ZIP+4 (12345-6789).';
      }
      return "This address didn't save. Check the street, city, state and ZIP, then save again.";
    }
    if (err.status === 404) return 'Someone changed this address while you had it open. Reload the page, then make your change again.';
    if (isDuplicateAddress(err)) return "This client already has this address. Edit the one that's there instead.";
    if (err.status === 409) return 'Someone else just changed these addresses. Reload the page, then make your change again.';
  }
  return (
    saveFailureText(err, contact) ??
    contactSentence("That didn't save. Try again, and if it happens again, tell {contact_system}.", contact)
  );
}

/** A failure shown under a list or in a dialog: a sentence already worded, or
 *  the failure itself, worded only once it shows, so the support contact is
 *  read only while there is a failure to show. */
type SaveFailure = string | { err: unknown };

function SaveFailureText({ failure }: { failure: SaveFailure }): ReactNode {
  return typeof failure === 'string' ? failure : <WordedSaveFailure err={failure.err} />;
}

function WordedSaveFailure({ err }: { err: unknown }): ReactNode {
  return saveErrorMessage(err, useSupportContact());
}

/** The server's refusal of an incomplete home, in the dialogs' words. */
const HOME_INCOMPLETE_TEXT = 'A home address needs a street, city, state and ZIP.';

/** The outcomes a save stops to report: the address saved, but the map
 *  shows it approximately, or not at all. */
export type ReportedGeocodeOutcome = Extract<ClientAddressGeocodeOutcome, 'approximate' | 'no_match' | 'unavailable'>;

const GEOCODE_OUTCOME_TEXT: Record<ReportedGeocodeOutcome, string> = {
  approximate: 'Saved. Only the town or ZIP was found, so the map shows this address as approximate.',
  no_match: "Saved, but the map couldn't find this address. Check the street, town and ZIP, then save again to put it on the map.",
  unavailable: "Saved, but the map couldn't check this address just now. Open it and save again later to put it on the map.",
};

/** The no-match line for an address saved with no state: the map places
 *  an untyped address only inside the tenant's working states, so the
 *  likely fix is the state. Names the working states in the server's
 *  order, or none when none is known. */
export function noMatchWithoutStateText(workingStates: readonly WorkingState[]): string {
  return `Saved. ${notPlacedLabel('not_found_add_state', workingStates)}`;
}

/** The outcome worth a line, or null when the save can simply close:
 *  a placed address, or a save that geocoded nothing. */
export function reportedGeocodeOutcome(outcome: ClientAddressGeocodeOutcome): ReportedGeocodeOutcome | null {
  return outcome === 'placed' || outcome === 'not_attempted' ? null : outcome;
}

/** EXPORTED: the one line every address dialog shows for a save the map
 *  could not fully place (the card's, the add dialog's and
 *  Edit contact's). A no-match saved with no state (typedStateCode null)
 *  asks for the state, naming the tenant's working states. */
export function GeocodeOutcomeLine({
  outcome,
  stateTyped,
}: {
  outcome: ReportedGeocodeOutcome;
  stateTyped: boolean;
}): ReactNode {
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const workingStates = readWorkingStates(useTenantConfig(tenantSlug, { enabled: tenantSlug !== '' }).data);
  const text = outcome === 'no_match' && !stateTyped ? noMatchWithoutStateText(workingStates) : GEOCODE_OUTCOME_TEXT[outcome];
  return (
    <p role="status" data-testid="address-geocode-outcome" className="text-xs text-sage-800">
      {text}
    </p>
  );
}

/** How a save was reached: after a check that matched, or with no check
 *  (a flags-only or unchanged edit); after a check that could not run;
 *  or through "Save as typed" after the check found nothing. */
export type AddressSaveHow = 'checked' | 'unavailable' | 'saved_as_typed';

/** What a dialog says once a save succeeded, or null to close.
 *  `stateTyped` is whether the saved address carried a state. */
export type SavedLine =
  | { kind: 'outcome'; outcome: ReportedGeocodeOutcome; stateTyped: boolean }
  | { kind: 'saved_as_typed' };

/** EXPORTED: the line a successful save leaves the dialog open on. A check
 *  that could not run is never silent: the save's own outcome, else the
 *  could-not-check line. A saved-as-typed address the save placed says
 *  its pin may not be exact; one the save placed only approximately, or
 *  not at all, says that instead. `typedState` is the saved address's
 *  state, which decides whether a no-match asks for the state. */
export function savedLineFor(
  outcome: ClientAddressGeocodeOutcome,
  how: AddressSaveHow,
  typedState: string | null,
): SavedLine | null {
  const stateTyped = typedStateCode(typedState) !== null;
  if (how === 'unavailable') return { kind: 'outcome', outcome: reportedGeocodeOutcome(outcome) ?? 'unavailable', stateTyped };
  if (how === 'saved_as_typed' && outcome === 'placed') return { kind: 'saved_as_typed' };
  const reported = reportedGeocodeOutcome(outcome);
  return reported === null ? null : { kind: 'outcome', outcome: reported, stateTyped };
}

/** EXPORTED: the saved line itself, in both dialogs. */
export function SavedLineView({ line }: { line: SavedLine }): ReactNode {
  if (line.kind === 'outcome') return <GeocodeOutcomeLine outcome={line.outcome} stateTyped={line.stateTyped} />;
  return (
    <p role="status" data-testid="address-saved-as-typed-line" className="text-xs text-sage-800">
      Saved as typed. Its map pin is the nearest match found and may not be exact.
    </p>
  );
}

// ---------------------------------------------------------------------
// The address check before a save
// ---------------------------------------------------------------------

/** Where a dialog's check stands: nothing asked, a check in flight, or a
 *  prompt waiting for a choice (with the address that was checked). */
export type AddressCheckPhase =
  | { kind: 'idle' }
  | { kind: 'checking' }
  | { kind: 'differs'; typed: AddressCheckFields; suggestion: AddressCheckFields }
  | { kind: 'not_found'; typed: AddressCheckFields };

/** What a check tells the dialog to do next. */
export type AddressCheckNext = { kind: 'save'; how: AddressSaveHow } | { kind: 'wait' };

/**
 * EXPORTED: one address check per dialog. `run` asks the server and
 * answers 'save' for a match or a check that could not run, and 'wait'
 * for a prompt (or when the dialog was cancelled meanwhile, so the code
 * after the check writes nothing). With `quietNotFound` (a non-primary
 * currently-at address, often a facility's name or a hospital wing) a
 * not_found answer shows no line and saves as after a match; a differs
 * answer still prompts. `clear` drops a prompt when a field changes;
 * `cancel` marks the dialog cancelled, as unmounting does.
 */
export function useAddressCheck(tenantSlug: string): {
  phase: AddressCheckPhase;
  run: (fields: AddressCheckFields, options?: { quietNotFound?: boolean }) => Promise<AddressCheckNext>;
  clear: () => void;
  cancel: () => void;
} {
  const [phase, setPhase] = useState<AddressCheckPhase>({ kind: 'idle' });
  const cancelled = useRef(false);
  useEffect(() => {
    cancelled.current = false;
    return () => {
      cancelled.current = true;
    };
  }, []);

  const run = useCallback(
    async (fields: AddressCheckFields, options: { quietNotFound?: boolean } = {}): Promise<AddressCheckNext> => {
      setPhase({ kind: 'checking' });
      const result = await checkClientAddress(tenantSlug, fields);
      if (cancelled.current) return { kind: 'wait' };
      if (result.outcome === 'differs' && result.suggestion !== null) {
        setPhase({ kind: 'differs', typed: fields, suggestion: result.suggestion });
        return { kind: 'wait' };
      }
      if (result.outcome === 'not_found' && !options.quietNotFound) {
        setPhase({ kind: 'not_found', typed: fields });
        return { kind: 'wait' };
      }
      setPhase({ kind: 'idle' });
      return { kind: 'save', how: result.outcome === 'unavailable' ? 'unavailable' : 'checked' };
    },
    [tenantSlug],
  );
  const clear = useCallback(() => setPhase((p) => (p.kind === 'differs' || p.kind === 'not_found' ? { kind: 'idle' } : p)), []);
  const cancel = useCallback(() => {
    cancelled.current = true;
  }, []);
  return { phase, run, clear, cancel };
}

function asAddressParts(f: AddressCheckFields): { line1: string; line2?: string; city?: string; state?: string; zip?: string } {
  return {
    line1: f.line1,
    line2: f.line2 ?? undefined,
    city: f.city ?? undefined,
    state: f.state ?? undefined,
    zip: f.zip ?? undefined,
  };
}

/** EXPORTED: the check's line, in both dialogs: "Did you mean" with the
 *  suggestion, or the not-found line. Nothing for any other phase. */
export function AddressCheckPrompt({ phase }: { phase: AddressCheckPhase }): ReactNode {
  if (phase.kind !== 'differs' && phase.kind !== 'not_found') return null;
  return (
    <p role="status" data-testid="address-check-line" className="text-xs text-sage-800">
      {phase.kind === 'differs'
        ? `Did you mean ${formatAddress(asAddressParts(phase.suggestion))}?`
        : "This address couldn't be found. Check the spelling and the house number, or save it as typed."}
    </p>
  );
}

/** EXPORTED: the choices a prompt offers, beside the dialog's own Cancel:
 *  "Keep what I typed" and "Use this" for a suggestion, "Save as typed"
 *  when nothing was found. */
export function AddressCheckChoices({
  phase,
  busy,
  onKeep,
  onUse,
  onSaveAsTyped,
}: {
  phase: AddressCheckPhase;
  busy: boolean;
  onKeep: () => void;
  onUse: () => void;
  onSaveAsTyped: () => void;
}): ReactNode {
  if (phase.kind === 'differs') {
    return (
      <>
        <Button variant="ghost" size="sm" data-testid="address-check-keep" disabled={busy} onClick={onKeep}>
          Keep what I typed
        </Button>
        <Button variant="primary" size="sm" data-testid="address-check-use" loading={busy} onClick={onUse}>
          Use this
        </Button>
      </>
    );
  }
  if (phase.kind === 'not_found') {
    return (
      <Button variant="primary" size="sm" data-testid="address-check-save-as-typed" loading={busy} onClick={onSaveAsTyped}>
        Save as typed
      </Button>
    );
  }
  return null;
}

// ---------------------------------------------------------------------
// The five address fields
// ---------------------------------------------------------------------

/** The five address fields (Street through ZIP), controlled, and
 *  EXPORTED so the contact dialog shares this one definition instead of
 *  growing a second copy of the inputs. An id prefix (or, absent one, a
 *  generated id) keeps element ids unique when both dialogs could exist
 *  on the page. `line1Required` shows the required marker on Street; the
 *  card always passes true, the contact dialog only once an address
 *  field holds a value. `homeRequired` marks City, State and ZIP required
 *  (a home needs all four); `stateRequired` marks State alone (a new
 *  address while no working state is known). `line1Error` / `line1Help`
 *  render under Street, tied to it by `aria-describedby`, for the contact
 *  dialog's Street-empty and clear-all states; `cityError`, `stateError`
 *  and `zipError` render under their fields the same way, and the parent
 *  supplies each only once the field has blurred blank (or at once, for a
 *  gap in a row that opened as the home), clearing it on the next change.
 *  `onFieldBlur` reports each of those three fields' blur. `disabled`
 *  locks all five, while a check runs or once a dialog has saved. */
export interface AddressFieldsValues {
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
}

export interface AddressFieldsProps {
  values: AddressFieldsValues;
  onLine1Change: (v: string) => void;
  onLine2Change: (v: string) => void;
  onCityChange: (v: string) => void;
  onStateChange: (v: string) => void;
  onZipChange: (v: string) => void;
  onFieldBlur?: (field: 'city' | 'state' | 'zip') => void;
  idPrefix?: string;
  line1Required?: boolean;
  homeRequired?: boolean;
  stateRequired?: boolean;
  /** City carries the required marker on its own (a Directory address:
   *  a town and a state at least, the street optional). */
  cityRequired?: boolean;
  line1Error?: string;
  line1Help?: string;
  cityError?: string;
  stateError?: string;
  zipError?: string;
  disabled?: boolean;
  /** 'place': the client address dialog's words (Street address,
   *  Apartment, suite or unit, City or town); every other form keeps the
   *  postal words. */
  wording?: 'postal' | 'place';
}

export function AddressFields({
  values,
  onLine1Change,
  onLine2Change,
  onCityChange,
  onStateChange,
  onZipChange,
  onFieldBlur,
  idPrefix,
  line1Required = false,
  homeRequired = false,
  stateRequired = false,
  cityRequired = false,
  line1Error,
  line1Help,
  cityError,
  stateError,
  zipError,
  disabled = false,
  wording = 'postal',
}: AddressFieldsProps): ReactNode {
  const words =
    wording === 'place'
      ? { line1: 'Street address', line2: 'Apartment, suite or unit', city: 'City or town' }
      : { line1: 'Address line 1', line2: 'Address line 2', city: 'City' };
  const autoPrefix = useId();
  const prefix = idPrefix ?? autoPrefix;
  const line1Id = `${prefix}-address-line1`;
  const line2Id = `${prefix}-address-line2`;
  const cityId = `${prefix}-address-city`;
  const stateId = `${prefix}-address-state`;
  const zipId = `${prefix}-address-zip`;
  const line1DescribedBy = line1Error
    ? `${line1Id}-error`
    : line1Help
      ? `${line1Id}-help`
      : undefined;
  const stateIsRequired = homeRequired || stateRequired;
  const cityIsRequired = homeRequired || cityRequired;
  // The "(as entered)" option: a legacy value (a full name, a lowercase
  // code, or anything else that isn't exactly one of the 56 codes) shown
  // and selected until the person picks a real option from the list.
  const legacyState = values.state !== '' && !isUsStateCode(values.state) ? values.state : null;

  return (
    <>
      <Field
        label={words.line1}
        required={line1Required}
        error={line1Error}
        help={line1Help}
        htmlFor={line1Id}
      >
        <TextInput
          id={line1Id}
          data-testid="address-line1-input"
          type="text"
          nameCase="place"
          className={FLD}
          value={values.line1}
          disabled={disabled}
          aria-describedby={line1DescribedBy}
          onChange={(e) => onLine1Change(e.target.value)}
        />
      </Field>
      <Field label={words.line2} htmlFor={line2Id}>
        <TextInput
          id={line2Id}
          data-testid="address-line2-input"
          type="text"
          nameCase="place"
          className={FLD}
          value={values.line2}
          disabled={disabled}
          onChange={(e) => onLine2Change(e.target.value)}
        />
      </Field>
      <Field label={words.city} htmlFor={cityId} required={cityIsRequired} error={cityError}>
        <TextInput
          id={cityId}
          data-testid="address-city-input"
          type="text"
          nameCase="place"
          className={FLD}
          value={values.city}
          disabled={disabled}
          aria-required={cityIsRequired ? 'true' : undefined}
          aria-invalid={cityError !== undefined}
          aria-describedby={cityError ? `${cityId}-error` : undefined}
          onChange={(e) => onCityChange(e.target.value)}
          onBlur={() => onFieldBlur?.('city')}
        />
      </Field>
      {/* State and ZIP share a row only when each column is wide enough
          for the longest name in the State list at the current text
          size: 14.5rem is that name in the field's own text plus the
          field's padding and border. Narrower than two of those, the
          pair stacks, so State is never cut short. */}
      <div className="grid gap-2 grid-cols-[repeat(auto-fit,minmax(min(14.5rem,100%),1fr))]">
        <Field label="State" htmlFor={stateId} required={stateIsRequired} error={stateError}>
          <SelectField
            size="field"
            id={stateId}
            data-testid="address-state-input"
            autoComplete="off"
            value={values.state}
            disabled={disabled}
            aria-required={stateIsRequired ? 'true' : undefined}
            aria-invalid={stateError !== undefined}
            aria-describedby={stateError ? `${stateId}-error` : undefined}
            onChange={(e) => onStateChange(e.target.value)}
            onBlur={() => onFieldBlur?.('state')}
          >
            {legacyState !== null && (
              <option value={legacyState}>{`${legacyState} (as entered)`}</option>
            )}
            <option value="">Choose a state</option>
            {US_STATES.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name}
              </option>
            ))}
            <optgroup label="Territories">
              {US_TERRITORIES.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.name}
                </option>
              ))}
            </optgroup>
          </SelectField>
        </Field>
        <Field label="ZIP" htmlFor={zipId} required={homeRequired} error={zipError}>
          <input
            id={zipId}
            data-testid="address-zip-input"
            type="text"
            inputMode="numeric"
            maxLength={10}
            autoComplete="off"
            className={FLD}
            value={values.zip}
            disabled={disabled}
            aria-required={homeRequired ? 'true' : undefined}
            aria-invalid={zipError !== undefined}
            aria-describedby={zipError ? `${zipId}-error` : undefined}
            onChange={(e) => onZipChange(e.target.value)}
            onBlur={() => onFieldBlur?.('zip')}
          />
        </Field>
      </div>
    </>
  );
}

/** The field messages a home (or a required State) shows. */
export const CITY_MISSING_TEXT = 'Add the city.';
export const STATE_MISSING_TEXT = 'Choose a state.';
export const ZIP_MISSING_TEXT = 'Add the ZIP.';

/** Which of City, State and ZIP have blurred blank (or opened blank on a
 *  row that was the home), so their message shows until the next change. */
export type FlaggedFields = { city: boolean; state: boolean; zip: boolean };

/** The fields flagged at once on open: each blank one, when the row opened
 *  as the home. */
export function flaggedOnOpen(row: { city?: string | null; state?: string | null; zip?: string | null } | null): FlaggedFields {
  const blank = (v: string | null | undefined): boolean => (v ?? '').trim() === '';
  return row === null
    ? { city: false, state: false, zip: false }
    : { city: blank(row.city), state: blank(row.state), zip: blank(row.zip) };
}

/** EXPORTED: the three field messages from the values, the flags and what
 *  is required. A present ZIP that fails keeps zipProblem's message. */
export function homeFieldMessages(args: {
  values: Pick<AddressFieldsValues, 'city' | 'state' | 'zip'>;
  flagged: FlaggedFields;
  homeRequired: boolean;
  stateRequired: boolean;
  /** City required on its own (a Directory address). */
  cityRequired?: boolean;
  storedZip: string | null;
}): { cityError?: string; stateError?: string; zipError?: string } {
  const { values, flagged, homeRequired, stateRequired, cityRequired = false, storedZip } = args;
  const blank = (v: string): boolean => v.trim() === '';
  const out: { cityError?: string; stateError?: string; zipError?: string } = {};
  if (flagged.city && (homeRequired || cityRequired) && blank(values.city)) out.cityError = CITY_MISSING_TEXT;
  if (flagged.state && (homeRequired || stateRequired) && blank(values.state)) out.stateError = STATE_MISSING_TEXT;
  if (flagged.zip) {
    if (blank(values.zip)) {
      if (homeRequired) out.zipError = ZIP_MISSING_TEXT;
    } else {
      out.zipError = zipProblem(values.zip, storedZip) ?? undefined;
    }
  }
  return out;
}

// ---------------------------------------------------------------------
// One address form: the fields, the gaps, the check and the save
// ---------------------------------------------------------------------

/** What an address form requires: a home's street, city, state and ZIP;
 *  a street; or a Directory entry's town and state (the street optional,
 *  line 2 only beside a street). */
export type AddressFormRule = 'home' | 'street' | 'directory';

/** An address as its five parts; a blank part is null. */
export interface AddressFormParts {
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}

/** The line a Directory address with line 2 and no street shows under
 *  Street, as the server refuses it. */
export const LINE2_NEEDS_STREET_TEXT = 'Add the street, or clear line 2.';

export interface UseAddressFormArgs {
  /** The stored parts the form opens on; null opens it empty (a new
   *  address), or on what existingText suggests. */
  existing: AddressFormParts | null;
  /** An address held as text only (existing null): the fields open on
   *  the parts the split's parser reads from it, a suggestion saved only
   *  when Save is pressed, and empty when the split would keep the text. */
  existingText?: string | null;
  rule: AddressFormRule;
  /** The fields' id prefix; the first gap a save finds takes focus. */
  idPrefix: string;
  /** Flag a blank City, State and ZIP at once (a row that opened as the
   *  home). */
  flagOnOpen?: boolean;
  /** A not-found check answer saves as a match (a currently-at address
   *  that is not the home), read when Save is pressed. */
  quietNotFound?: () => boolean;
  /** Fields are locked: a save went through and its line shows. */
  done: boolean;
  saving: boolean;
  onSave: (parts: AddressFormParts, how: AddressSaveHow) => void;
  /** False for a form that draws no address (a record dialog whose
   *  projection has none): nothing is fetched. Default true. */
  enabled?: boolean;
}

export interface AddressForm {
  /** Spread onto AddressFields, with the caller's own idPrefix. */
  fields: Omit<AddressFieldsProps, 'idPrefix'>;
  check: ReturnType<typeof useAddressCheck>;
  checking: boolean;
  prompting: boolean;
  locked: boolean;
  /** Any other change on the form clears a prompt and a gap. */
  touched: () => void;
  pressSave: () => void;
  /** Keep what I typed, Use this, or Save as typed. */
  choose: (how: AddressSaveHow, useSuggestion: boolean) => void;
  cancel: () => void;
  /** Refill the fields (a form that prefills after it opens): the parts,
   *  or empty with State back on the working state. */
  reset: (parts: AddressFormParts | null) => void;
  /** No street, line 2, town or ZIP typed: a form whose address is
   *  optional sends none. State alone, which starts filled, is no
   *  address. */
  isEmpty: () => boolean;
}

/**
 * EXPORTED: one address form's flow, shared by the client's address
 * dialog, the postal dialog and every form that embeds the fields. A new
 * address (or one held as text only) starts State on the tenant's first
 * working state until it is touched. Save names each gap under its box
 * and moves focus to the first; a save whose text changed is checked
 * first, except a PO Box line, an entry with no street (a town and a
 * state) and a seat the check refuses, which save straight away; an
 * unchanged edit saves with no check.
 */
export function useAddressForm(args: UseAddressFormArgs): AddressForm {
  const { existing, rule, idPrefix, done, saving } = args;
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const workingStates = readWorkingStates(useTenantConfig(tenantSlug, { enabled: args.enabled ?? true }).data);
  const check = useAddressCheck(tenantSlug);
  const checkAdmits = canCheckAddress(useTenant().whoami);
  const isNew = existing === null;
  // What the fields open on: the stored parts, else what the text-only
  // address suggests, read once when the form opens.
  const [opening] = useState<AddressFormParts | null>(() => existing ?? suggestAddressParts(args.existingText, rule));

  const [line1, setLine1] = useState(opening?.line1 ?? '');
  const [line2, setLine2] = useState(opening?.line2 ?? '');
  const [city, setCity] = useState(opening?.city ?? '');
  const [stateValue, setStateValue] = useState(opening?.state ?? '');
  // A suggested state stands as typed: the working state does not replace it.
  const [stateTouched, setStateTouched] = useState(isNew && opening !== null);
  const state = isNew && !stateTouched ? newAddressStartState(workingStates) : stateValue;
  const [zip, setZip] = useState(opening?.zip ?? '');
  const [flagged, setFlagged] = useState<FlaggedFields>(() => flaggedOnOpen(args.flagOnOpen ? existing : null));
  // The gap the last press found under Street; the next change clears it.
  const [line1Gap, setLine1Gap] = useState<string | null>(null);
  const [pending, setPending] = useState<AddressFormParts | null>(null);

  const checking = check.phase.kind === 'checking';
  const prompting = check.phase.kind === 'differs' || check.phase.kind === 'not_found';
  const locked = done || checking;

  const directory = rule === 'directory';
  const homeRequired = rule === 'home';
  // A Directory entry always needs its state; any other new address needs
  // it while no working state is known (none, not answered yet, or
  // unreadable).
  const stateRequired = directory || (isNew && workingStates.length === 0);
  const storedZip = existing?.zip ?? null;
  const values = { line1, line2, city, state, zip };
  const messages = homeFieldMessages({ values, flagged, homeRequired, stateRequired, cityRequired: directory, storedZip });
  const line2WithoutStreet = directory && line2.trim() !== '' && line1.trim() === '';
  const line1Error = line2WithoutStreet ? LINE2_NEEDS_STREET_TEXT : (line1Gap ?? undefined);

  function touched(): void {
    check.clear();
    setLine1Gap(null);
  }
  function changed(set: (v: string) => void): (v: string) => void {
    return (v: string) => {
      touched();
      set(v);
    };
  }
  function changedField(field: keyof FlaggedFields, set: (v: string) => void): (v: string) => void {
    return (v: string) => {
      check.clear();
      setFlagged((f) => ({ ...f, [field]: false }));
      set(v);
    };
  }

  const nullable = (v: string): string | null => (v.trim() === '' ? null : v.trim());
  function buildParts(): AddressFormParts {
    return {
      line1: nullable(line1),
      line2: nullable(line2),
      city: nullable(city),
      state: nullable(state),
      zip: zip.trim() === '' ? null : normalizeZip(zip),
    };
  }

  function pressSave(): void {
    if (saving || checking) return;
    const streetGap = !directory && line1.trim() === '';
    const zipBad = zipProblem(zip, storedZip) !== null;
    const missing = new Set<string>(homeRequired ? missingHomeFields(values) : []);
    if (stateRequired && state.trim() === '') missing.add('state');
    if (directory && city.trim() === '') missing.add('city');
    if (!streetGap && !line2WithoutStreet && !zipBad && missing.size === 0) {
      void saveNow();
      return;
    }
    if (streetGap) setLine1Gap(fieldSentence('Address line 1', { kind: 'text' }, line1));
    if (zipBad || missing.size > 0) setFlagged({ city: true, state: true, zip: true });
    const part =
      streetGap || line2WithoutStreet
        ? 'line1'
        : (['city', 'state', 'zip'] as const).find((f) => missing.has(f) || (f === 'zip' && zipBad));
    if (part !== undefined) document.getElementById(`${idPrefix}-address-${part}`)?.focus();
  }

  async function saveNow(): Promise<void> {
    const parts = buildParts();
    // An unchanged edit goes straight to the save.
    if (existing !== null && !addressTextChanged(asCheckFields(existing), asCheckFields(parts))) {
      args.onSave(parts, 'checked');
      return;
    }
    // A PO Box and a town with no street are placed at town level,
    // approximate by design, and a seat the check refuses gets no check:
    // each saves straight away.
    if (parts.line1 === null || isPoBoxLine(parts.line1) || !checkAdmits) {
      args.onSave(parts, 'checked');
      return;
    }
    setPending(parts);
    const next = await check.run(asCheckFields(parts), { quietNotFound: args.quietNotFound?.() ?? false });
    if (next.kind === 'save') args.onSave(parts, next.how);
  }

  function choose(how: AddressSaveHow, useSuggestion: boolean): void {
    const phase = check.phase;
    if (pending === null) return;
    const parts = useSuggestion && phase.kind === 'differs' ? { ...pending, ...phase.suggestion } : pending;
    check.clear();
    args.onSave(parts, how);
  }

  return {
    fields: {
      values,
      onLine1Change: changed(setLine1),
      onLine2Change: changed(setLine2),
      onCityChange: changedField('city', setCity),
      onStateChange: changedField('state', (v) => {
        setStateTouched(true);
        setStateValue(v);
      }),
      onZipChange: changedField('zip', setZip),
      onFieldBlur: (field) => setFlagged((f) => ({ ...f, [field]: true })),
      line1Required: !directory,
      homeRequired,
      stateRequired,
      cityRequired: directory,
      line1Error,
      cityError: messages.cityError,
      stateError: messages.stateError,
      zipError: messages.zipError,
      disabled: locked,
    },
    check,
    checking,
    prompting,
    locked,
    touched,
    pressSave,
    choose,
    cancel: check.cancel,
    reset: (parts) => {
      touched();
      setLine1(parts?.line1 ?? '');
      setLine2(parts?.line2 ?? '');
      setCity(parts?.city ?? '');
      setStateValue(parts?.state ?? '');
      setStateTouched(parts !== null);
      setZip(parts?.zip ?? '');
      setFlagged({ city: false, state: false, zip: false });
    },
    isEmpty: () => [line1, line2, city, zip].every((v) => v.trim() === ''),
  };
}

/** The parts as the check and the change test read them. */
function asCheckFields(p: AddressFormParts): AddressCheckFields {
  return { line1: p.line1 ?? '', line2: p.line2, city: p.city, state: p.state, zip: p.zip };
}

// ---------------------------------------------------------------------
// The postal address dialog (an address with no label or flags)
// ---------------------------------------------------------------------

/** What a postal save did to the map: a client address save's outcomes,
 *  plus a Directory person shown at their organization's pin, whom the
 *  map never asks. */
export type PostalSaveOutcome = ClientAddressGeocodeOutcome | 'shown_with_organization';

export interface PostalAddressDialogProps {
  /** The stored parts; null for a new address or one held as text only. */
  existing: AddressFormParts | null;
  /** The address as text, shown as "On file as" above empty fields when
   *  the row has text and no parts; nothing is prefilled from it. */
  existingText?: string | null;
  rule: AddressFormRule;
  /** Saves the parts; answers what the save did to the map
   *  ('not_attempted' for an address never placed). */
  onSave: (parts: AddressFormParts, how: AddressSaveHow) => Promise<PostalSaveOutcome>;
  onClose: () => void;
}

/**
 * EXPORTED: the client address dialog without the label and the two
 * flags, for an address that is only an address: a Directory listing's,
 * a staff home, a billing contact's. The same fields, check, prompt and
 * saved line. A town-only save that the map placed at its town closes
 * as a placed save does: a town-only entry is approximate by design.
 */
export function PostalAddressDialog({ existing, existingText = null, rule, onSave, onClose }: PostalAddressDialogProps): ReactNode {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<SaveFailure | null>(null);
  const [saved, setSaved] = useState<SavedLine | null>(null);
  const form = useAddressForm({
    existing,
    existingText,
    rule,
    idPrefix: 'postal',
    done: saved !== null,
    saving,
    onSave: (parts, how) => {
      setError(null);
      setSaving(true);
      onSave(parts, how).then(
        (outcome) => {
          setSaving(false);
          // A town-only entry placed at its town, and a person shown with
          // their organization, close as a placed save does: neither is
          // a miss worth a line.
          const townOnly = parts.line1 === null && outcome === 'approximate';
          const line =
            townOnly || outcome === 'shown_with_organization' ? null : savedLineFor(outcome, how, parts.state);
          if (line === null) onClose();
          else setSaved(line);
        },
        (err: unknown) => {
          setSaving(false);
          setError({ err });
        },
      );
    },
  });
  const textOnly = existing === null && (existingText ?? '').trim() !== '';

  function cancel(): void {
    form.cancel();
    onClose();
  }

  return (
    <Dialog
      title={existing !== null || textOnly ? 'Edit address' : 'Add address'}
      size="md"
      testId="postal-address-dialog"
      onClose={cancel}
      footer={
        saved !== null ? (
          <Button variant="primary" size="sm" data-testid="postal-address-done" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="ghost" size="sm" onClick={cancel}>
              Cancel
            </Button>
            {form.prompting ? (
              <AddressCheckChoices
                phase={form.check.phase}
                busy={saving}
                onKeep={() => form.choose('checked', false)}
                onUse={() => form.choose('checked', true)}
                onSaveAsTyped={() => form.choose('saved_as_typed', false)}
              />
            ) : (
              <Button
                variant="primary"
                size="sm"
                disabled={saving || form.checking}
                loading={saving || form.checking}
                data-testid="postal-address-save"
                onClick={form.pressSave}
              >
                Save address
              </Button>
            )}
          </>
        )
      }
    >
      <div className="flex flex-col gap-2 py-2">
        {textOnly && (
          <p data-testid="address-on-file-text" className="text-xs text-sage-800">
            On file as: {existingText}
          </p>
        )}
        <AddressFields idPrefix="postal" {...form.fields} />
        <AddressCheckPrompt phase={form.check.phase} />
        {saved !== null && <SavedLineView line={saved} />}
        {error !== null && (
          <p role="alert" data-testid="postal-address-error" className="text-xs text-pepper">
            <SaveFailureText failure={error} />
          </p>
        )}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------
// The card's address dialog
// ---------------------------------------------------------------------

interface AddressDialogProps {
  /** null = add a new address; a row = edit it in place. */
  existing: AddressRow | null;
  /** A new address starts with Primary checked (an edit reads the row). */
  defaultPrimary?: boolean;
  onSave: (input: ClientAddressInput, how: AddressSaveHow) => void;
  saving: boolean;
  error: SaveFailure | null;
  /** Set once the save succeeded with something worth saying: the fields
   *  lock, the line shows, and Done is the only action. */
  saved: SavedLine | null;
  onClose: () => void;
  /** The client's pronouns, for the two ticks. */
  pronouns?: ClientPronouns | null;
}

/** The ticks' words, with the client's pronoun. */
function addressWords(pronouns: ClientPronouns | null | undefined) {
  const f = pronounForms(pronouns);
  const cap = (t: string): string => t.charAt(0).toUpperCase() + t.slice(1);
  return {
    home: `${cap(f.possessive)} home address (the primary one)`,
    current: `Where ${f.subject} ${f.plural ? 'are' : 'is'} now (currently at)`,
  };
}

const PLACE_TYPE_ID = 'address-place-type';
const PLACE_NAME_ID = 'address-place-name-input';

/** The longest place name (the server's MAX_PLACE_NAME). */
const MAX_PLACE_NAME = 200;

/** The line under the two ticks. */
export const ADDRESS_ROLES_LINE =
  'A client has one home address and one current place. Ticking a box here moves that role to this address.';

/** Whether a type of place draws the name box: every type but Home and
 *  Mailing (the server's placeNameKept). */
function placeNameShown(placeType: string): boolean {
  return placeType !== '' && placeType !== 'home' && placeType !== 'mailing';
}

/** A row's chip: its type of place, else its label (an untyped row). */
function placeChip(a: AddressRow): string {
  return a.placeType !== undefined ? (PLACE_TYPE_WORDS[a.placeType] ?? humanizeToken(a.placeType)) : humanizeToken(a.label);
}

function AddressDialog({
  existing,
  defaultPrimary = false,
  onSave,
  saving,
  error,
  saved,
  onClose,
  pronouns = null,
}: AddressDialogProps): ReactNode {
  const words = addressWords(pronouns);
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const [placeType, setPlaceType] = useState<string>(existing?.placeType ?? '');
  const [placeSince, setPlaceSince] = useState<string>(existing?.placeSince ?? '');
  const [typeError, setTypeError] = useState<string | undefined>(undefined);
  const [placeName, setPlaceName] = useState<string>(existing?.placeName ?? '');
  // True after a Directory pick, until the next keystroke in the name box.
  const [placeLinked, setPlaceLinked] = useState(false);
  const [isPrimary, setIsPrimary] = useState(existing?.isPrimary ?? defaultPrimary);
  const [isCurrent, setIsCurrent] = useState(existing?.isCurrentLocation ?? false);
  const done = saved !== null;
  const mailing = placeType === 'mailing';

  // The flow is the shared form's; this dialog adds the label and the two
  // flags around it. A home needs all four parts; any other address its
  // street. A currently-at address that is not the home is never told
  // "couldn't find"; the home keeps the full check.
  const form = useAddressForm({
    existing: existing === null ? null : postalFieldsOfRow(existing),
    rule: isPrimary ? 'home' : 'street',
    idPrefix: 'address',
    flagOnOpen: existing?.isPrimary ?? false,
    quietNotFound: () => isCurrent && !isPrimary,
    done,
    saving,
    onSave: (parts, how) =>
      onSave(
        {
          ...(existing !== null ? { id: existing.id } : {}),
          // The server stores the label the type decides; it is sent so a
          // reader of the body sees the same word.
          label: PLACE_TYPE_LABEL_KIND[placeType] ?? existing?.label ?? 'home',
          line1: parts.line1 ?? '',
          line2: parts.line2,
          city: parts.city,
          state: parts.state,
          zip: parts.zip,
          is_current_location: mailing ? false : isCurrent,
          is_primary: mailing ? false : isPrimary,
          // The place fields travel only when they differ from the stored
          // row (absent = unchanged on an edit, none on a create).
          ...placeChanges(),
        },
        how,
      ),
  });

  /** The place fields that differ from the stored row: on a create, the
   *  type and whatever else is set. A type that draws no name box sends
   *  no name (the server stores none on it). */
  function placeChanges(): { place_type?: string; place_since?: string | null; place_name?: string | null } {
    const since = mailing || placeSince === '' ? null : placeSince;
    const name = placeNameShown(placeType) && placeName.trim() !== '' ? placeName.trim() : null;
    return {
      ...(placeType !== (existing?.placeType ?? '') ? { place_type: placeType } : {}),
      ...(since !== (existing?.placeSince ?? null) ? { place_since: since } : {}),
      ...(name !== (existing?.placeName ?? null) ? { place_name: name } : {}),
    };
  }

  /** A Directory pick: the name, and the entry's address into the five
   *  boxes, all editable. An entry with no street keeps what the boxes
   *  hold. */
  async function pickFacility(entry: { id: string; name: string }): Promise<void> {
    form.touched();
    setPlaceName(entry.name);
    setPlaceLinked(true);
    let d: Awaited<ReturnType<typeof api.getProvider>>;
    try {
      d = await api.getProvider(tenantSlug, entry.id);
    } catch {
      // The name stays; the boxes keep what they hold.
      return;
    }
    if (d.address_line1 === undefined || d.address_line1 === null || d.address_line1.trim() === '') return;
    form.reset({
      line1: d.address_line1,
      line2: d.address_line2 ?? null,
      city: d.address_city ?? null,
      state: d.address_state ?? null,
      zip: d.address_zip ?? null,
    });
  }

  /** Save: the type of place first (required on every save), then the
   *  address form's own gaps and check. */
  function pressSave(): void {
    if (placeType === '') {
      setTypeError('Choose a type of place.');
      document.getElementById(PLACE_TYPE_ID)?.focus();
      return;
    }
    form.pressSave();
  }
  const locked = form.locked;

  function changed<T>(set: (v: T) => void): (v: T) => void {
    return (v: T) => {
      form.touched();
      set(v);
    };
  }

  function cancel(): void {
    form.cancel();
    onClose();
  }

  return (
    <Dialog
      title={existing !== null ? 'Edit address' : 'Add address'}
      size="md"
      testId="address-dialog"
      onClose={cancel}
      footer={
        done ? (
          <Button variant="primary" size="sm" data-testid="address-geocode-done" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="ghost" size="sm" onClick={cancel}>
              Cancel
            </Button>
            {form.prompting ? (
              <AddressCheckChoices
                phase={form.check.phase}
                busy={saving}
                onKeep={() => form.choose('checked', false)}
                onUse={() => form.choose('checked', true)}
                onSaveAsTyped={() => form.choose('saved_as_typed', false)}
              />
            ) : (
              <Button
                variant="primary"
                size="sm"
                disabled={saving || form.checking}
                loading={saving || form.checking}
                data-testid="address-save"
                onClick={pressSave}
              >
                {existing !== null ? 'Save address' : 'Add address'}
              </Button>
            )}
          </>
        )
      }
    >
      <div className="flex flex-col gap-2 py-2">
        {/* Type of place and Since share a row while both fit; with larger
            text Since wraps beneath, so no option is ever cut. In-home
            support follows for a Home or an Apartment. */}
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          <div className="min-w-0 flex-[1_1_16rem]">
          <Field label="Type of place" htmlFor={PLACE_TYPE_ID} required error={typeError}>
            <SelectField
              size="field"
              id={PLACE_TYPE_ID}
              data-testid="address-place-type"
              value={placeType}
              disabled={locked}
              aria-required="true"
              aria-invalid={typeError !== undefined}
              aria-describedby={typeError !== undefined ? `${PLACE_TYPE_ID}-error` : undefined}
              onChange={(e) => {
                const next = e.target.value;
                form.touched();
                setTypeError(undefined);
                setPlaceType(next);
                if (next === 'mailing') {
                  setIsPrimary(false);
                  setIsCurrent(false);
                }
              }}
            >
              <option value="">Choose a type</option>
              {PLACE_TYPES.map((v) => (
                <option key={v} value={v}>
                  {PLACE_TYPE_WORDS[v] ?? humanizeToken(v)}
                </option>
              ))}
            </SelectField>
          </Field>
          </div>
          {placeType !== '' && !mailing && (
            <div className="min-w-0 flex-[1_1_10rem]">
            <Field label="Since" htmlFor="address-place-since-input">
              <DateInput
                id="address-place-since-input"
                data-testid="address-place-since"
                size="field" className="w-full"
                value={placeSince}
                disabled={locked}
                onChange={(e) => {
                  form.touched();
                  setPlaceSince(e.target.value);
                }}
              />
            </Field>
            </div>
          )}
        </div>
        {placeNameShown(placeType) && (
          <Field label="Facility or place name" htmlFor={PLACE_NAME_ID}>
            {(CARE_SETTING_PLACE_TYPES as readonly string[]).includes(placeType) ? (
              <DirectoryNameBox
                id={PLACE_NAME_ID}
                testId="address-place-name"
                value={placeName}
                onChange={(next) => {
                  form.touched();
                  setPlaceName(next);
                  setPlaceLinked(false);
                }}
                onPick={(entry) => void pickFacility(entry)}
                only="organization"
                listLabel="Directory facilities"
                linked={placeLinked}
                boxAria={fieldBoxAria(PLACE_NAME_ID, {})}
              />
            ) : (
              <TextInput
                id={PLACE_NAME_ID}
                data-testid="address-place-name"
                className={FLD}
                maxLength={MAX_PLACE_NAME}
                value={placeName}
                disabled={locked}
                onChange={(e) => {
                  form.touched();
                  setPlaceName(e.target.value);
                }}
              />
            )}
          </Field>
        )}
        <AddressFields idPrefix="address" wording="place" {...form.fields} />
        <label className="flex min-h-11 cursor-pointer items-center gap-2.5 text-[15px] text-sage-800">
          <input
            type="checkbox"
            data-testid="address-primary-checkbox"
            className="h-5 w-5 shrink-0 accent-sage-700"
            checked={isPrimary}
            disabled={locked || mailing}
            onChange={(e) => changed(setIsPrimary)(e.target.checked)}
          />
          {words.home}
        </label>
        <label className="flex min-h-11 cursor-pointer items-center gap-2.5 text-[15px] text-sage-800">
          <input
            type="checkbox"
            data-testid="address-current-checkbox"
            className="h-5 w-5 shrink-0 accent-sage-700"
            checked={isCurrent}
            disabled={locked || mailing}
            onChange={(e) => changed(setIsCurrent)(e.target.checked)}
          />
          {words.current}
        </label>
        <p className="text-xs text-sage-800">{ADDRESS_ROLES_LINE}</p>
        <AddressCheckPrompt phase={form.check.phase} />
        {saved !== null && <SavedLineView line={saved} />}
        {error !== null && (
          <p role="alert" data-testid="address-error" className="text-xs text-pepper">
            <SaveFailureText failure={error} />
          </p>
        )}
      </div>
    </Dialog>
  );
}

/** The five postal fields of an input, as the check reads them. */
export function postalFieldsOf(input: ClientAddressInput): AddressCheckFields {
  return { line1: input.line1, line2: input.line2, city: input.city, state: input.state, zip: input.zip };
}

/** The five postal fields of a stored row. */
export function postalFieldsOfRow(row: AddressRow): AddressCheckFields {
  return {
    line1: row.line1,
    line2: row.line2 ?? null,
    city: row.city ?? null,
    state: row.state ?? null,
    zip: row.zip ?? null,
  };
}

/**
 * The address dialog with its own save: the SAME AddressDialog and save
 * mutation the card uses, so there is one form and one check. `existing`
 * edits that row in place; null adds one, with Primary checked when
 * `defaultPrimary`. The Contact band's Edit saves the address itself
 * instead, through the same mutation, alongside phone and email.
 */
export function AddressEditDialog({
  existing,
  defaultPrimary = false,
  onClose,
  pronouns = null,
}: {
  existing: AddressRow | null;
  defaultPrimary?: boolean;
  onClose: () => void;
  pronouns?: ClientPronouns | null;
}): ReactNode {
  const { tenantSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  const save = useSaveClientAddress(tenantSlug, clientId);
  const [error, setError] = useState<SaveFailure | null>(null);
  const [saved, setSaved] = useState<SavedLine | null>(null);
  return (
    <AddressDialog
      existing={existing}
      defaultPrimary={defaultPrimary}
      pronouns={pronouns}
      saving={save.isPending}
      error={error}
      saved={saved}
      onClose={onClose}
      onSave={(input, how) => {
        setError(null);
        save.mutate(input, {
          onSuccess: (result) => {
            const line = savedLineFor(result.geocode_outcome, how, input.state);
            if (line === null) onClose();
            else setSaved(line);
          },
          onError: (err) => setError({ err }),
        });
      }}
    />
  );
}

/** The address dialog in create mode for a client with no address, so
 *  the new address is the primary one. The card's own "+ Add address"
 *  composes AddressDialog inline with the same default (LP-29). */
export function AddAddressDialog({
  onClose,
  pronouns = null,
}: {
  onClose: () => void;
  pronouns?: ClientPronouns | null;
}): ReactNode {
  return <AddressEditDialog existing={null} defaultPrimary onClose={onClose} pronouns={pronouns} />;
}

export function AddressesCard({
  addresses,
  pronouns = null,
}: {
  addresses: AddressRow[];
  pronouns?: ClientPronouns | null;
}): ReactNode {
  const { tenantSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  const save = useSaveClientAddress(tenantSlug, clientId);
  const lifecycle = useSetClientAddressArchived(tenantSlug, clientId);
  const [dialog, setDialog] = useState<null | { existing: AddressRow | null }>(null);
  const [error, setError] = useState<SaveFailure | null>(null);
  const [saved, setSaved] = useState<SavedLine | null>(null);
  const [lifecycleError, setLifecycleError] = useState<SaveFailure | null>(null);
  // The address writes open to write_clinical_notes or
  // manage_client_identity (22l1): a care manager adds where the client is
  // now and types a place; the home address stays the office's, so only
  // an office seat archives the primary. A read-only viewer
  // (board_observer holds neither cap) sees no write door, and neither
  // does a SPICE-only therapist, who reads the record but does not keep
  // it (canWriteClinicalRecord, as every other door on the tab).
  const whoami = useTenant().whoami;
  const officeSeat = canManageClientIdentity(whoami);
  const canEdit = (officeSeat || canWriteClinicalNotes(whoami)) && canWriteClinicalRecord(whoami);
  // PART 5 (UI polish pass): AddressesCard is hand-rolled like AllergiesCard
  // was — it learns the SAME shared collapse machinery (never a local
  // copy). Default expanded; no hash anchor (Addresses isn't a jump-strip
  // section today).
  const active = addresses.filter((a) => a.isActive);
  const archived = addresses.filter((a) => !a.isActive);
  // Item 5, LP-40: archived addresses leave the screen by default; the
  // page's one archived line shows them beneath the active rows (entry
  // order). The card carries no reveal of its own.
  const archivedRevealed = useArchivedItems(archived.length);
  const { open, toggle } = usePersistedCardOpen(
    'card-addresses',
    undefined,
    false,
    archivedRevealed && archived.length > 0,
  );

  // The collapsed one-line summary reads homeFact, as the Contact band and
  // the face sheet do: the home address (the active primary), or "No
  // primary address" when the client has rows but no active primary, plus
  // "· currently at <facility>" when the current location is another row.
  const home = homeFact(addresses);
  const current = currentlyAt(addresses);
  const currentSuffix = current !== null ? ` · currently at ${addressLine(current)}` : '';
  const summaryLine =
    home.kind === 'home'
      ? `${addressLine(home.row)}${currentSuffix}`
      : home.kind === 'no_primary'
        ? `${NO_PRIMARY_TEXT}${currentSuffix}`
        : null;

  function openDialog(existing: AddressRow | null): void {
    setError(null);
    setSaved(null);
    setDialog({ existing });
  }

  function handleSave(input: ClientAddressInput, how: AddressSaveHow): void {
    setError(null);
    save.mutate(input, {
      onSuccess: (result) => {
        const line = savedLineFor(result.geocode_outcome, how, input.state);
        if (line === null) setDialog(null);
        else setSaved(line);
      },
      onError: (err) => setError({ err }),
    });
  }

  // Archive and restore report a refusal under the list; the next click
  // clears it.
  function setArchived(addressId: string, archived: boolean): void {
    setLifecycleError(null);
    lifecycle.mutate(
      { addressId, archived },
      {
        onError: (err) =>
          setLifecycleError(
            !archived && isDuplicateAddress(err)
              ? "This client already has this address, so it wasn't restored. Edit the one that's there instead."
              : { err },
          ),
      },
    );
  }

  return (
    <section
      data-testid="card-addresses"
      // PART 4 (domain color system): quiet sage (logistics) — one family
      // for the whole Life & logistics tab.
      className={`rounded-card bg-paper shadow-sh1 border border-sage-800/[8%] p-4 border-l-4 ${CARD_ACCENTS.logistics.band}`}
    >
      <div className={`flex items-start justify-between gap-2 ${open ? 'mb-2' : ''}`}>
        <div className="flex items-center gap-2 min-w-0">
          {/* The shared disclosure chevron (G5 anatomy). */}
          <Button
            variant="ghost"
            size="sm"
            className="!min-h-0 -ml-1 !px-1 !py-0.5"
            data-testid="section-collapse"
            aria-expanded={open}
            aria-controls={open ? 'addresses-body' : undefined}
            aria-label={open ? 'Collapse Addresses' : 'Expand Addresses'}
            icon={
              open ? (
                <ChevronDown size={16} aria-hidden="true" />
              ) : (
                <ChevronRight size={16} aria-hidden="true" />
              )
            }
            onClick={toggle}
          />
          {/* B5: the record's one heading scale (display serif, text-xl). */}
          <h3 className={`font-display text-xl ${CARD_ACCENTS.logistics.heading}`}>Addresses</h3>
        </div>
        {canEdit && (
          <Button
            variant="ghost"
            size="sm"
            data-testid="address-add-action"
            onClick={() => openDialog(null)}
          >
            + Add address
          </Button>
        )}
      </div>

      {/* COLLAPSED: a one-line summary (the primary address, plus
          "· currently at <facility>" when the client is elsewhere) so the
          fold never hides that the record has an address on file. */}
      {!open && (
        <p data-testid="addresses-collapsed-summary" className="text-sm text-sage-800">
          {summaryLine ?? "Add the client's home address to place them on the map."}
        </p>
      )}

      {open && active.length === 0 && (
        <p className="text-sm italic text-ink-soft" data-testid="addresses-empty">
          Add the client's home address to place them on the map.
        </p>
      )}

      {open && (
      <>
      <ul id="addresses-body" className="flex flex-col gap-3">
        {active.map((a) => (
          <li key={a.id} data-testid="address-row" className="text-sm text-sage-800">
            <div className="flex flex-wrap items-center gap-1.5">
              <Chip variant="neutral">{placeChip(a)}</Chip>
              {a.isPrimary && (
                <Chip variant="affirm" data-testid="address-primary-chip">
                  Primary
                </Chip>
              )}
              {a.isCurrentLocation && (
                // neutral, not a hue: "currently here" is a fact that
                // reads in words (the record-page design law).
                <Chip variant="neutral" data-testid="address-current-chip">
                  Currently here
                </Chip>
              )}
              {canEdit && (
                <span className="ml-auto inline-flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<Pencil size={14} aria-hidden="true" />}
                    data-testid={`address-edit-${a.id}`}
                    onClick={() => openDialog(a)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid={`address-archive-${a.id}`}
                    onClick={() => setArchived(a.id, true)}
                  >
                    Archive
                  </Button>
                </span>
              )}
            </div>
            {a.placeName !== undefined && (
              <div className="mt-0.5 font-semibold" data-testid="address-row-place-name">
                {a.placeName}
              </div>
            )}
            <div className={a.placeName !== undefined ? undefined : 'mt-0.5'}>
              {a.line1}
              {a.line2 ? `, ${a.line2}` : ''}
            </div>
            {formatCityStateZip(a) !== '' && (
              <div className="text-ink-soft" data-testid="address-city-line">
                {formatCityStateZip(a)}
              </div>
            )}
            {a.placeSince !== undefined && (
              <div className="text-ink-soft" data-testid="address-row-since">
                Since {proseDate(a.placeSince)}
              </div>
            )}
          </li>
        ))}
        {archivedRevealed && archived.map((a) => (
          <li
            key={a.id}
            data-testid="address-row-archived"
            className="text-sm text-ink-soft opacity-70"
          >
            <div className="flex flex-wrap items-center gap-1.5">
              <Chip variant="neutral">{placeChip(a)}</Chip>
              <Chip variant="neutral">Archived</Chip>
              {canEdit && (
                <span className="ml-auto">
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid={`address-restore-${a.id}`}
                    onClick={() => setArchived(a.id, false)}
                  >
                    Restore
                  </Button>
                </span>
              )}
            </div>
            {a.placeName !== undefined && (
              <div className="mt-0.5 font-semibold" data-testid="address-row-place-name">
                {a.placeName}
              </div>
            )}
            <div className={a.placeName !== undefined ? undefined : 'mt-0.5'}>
              {a.line1}
              {a.line2 ? `, ${a.line2}` : ''}
            </div>
            {formatCityStateZip(a) !== '' && (
              <div data-testid="address-city-line">{formatCityStateZip(a)}</div>
            )}
          </li>
        ))}
      </ul>

      {lifecycleError !== null && (
        <p role="alert" data-testid="address-lifecycle-error" className="mt-2 text-xs text-pepper">
          <SaveFailureText failure={lifecycleError} />
        </p>
      )}

      </>
      )}

      {dialog !== null && (
        <AddressDialog
          pronouns={pronouns}
          existing={dialog.existing}
          defaultPrimary={active.length === 0}
          onSave={handleSave}
          saving={save.isPending}
          error={error}
          saved={saved}
          onClose={() => setDialog(null)}
        />
      )}
    </section>
  );
}
